import { useMemo } from 'react';
import type { ColumnSchema, DuckDbType } from '@/types/api';
import type { GridMode } from '@/cache/types';
import { ColumnPicker } from '@/components/filter/ColumnPicker';
import {
  aggregatesFor,
  coerceAggregate,
  isGroupActive,
  moveGroupColumn,
  newAggregate,
  newGroupColumn,
  type AggregateFn,
  type GroupState,
} from './groupModel';

export interface GroupPanelProps {
  readonly columns: readonly ColumnSchema[];
  readonly state: GroupState;
  readonly mode: GridMode;
  readonly onChange: (state: GroupState) => void;
  readonly onApply: (state: GroupState | null) => void;
  readonly onClose: () => void;
}

export function GroupPanel({
  columns,
  state,
  mode,
  onChange,
  onApply,
  onClose,
}: GroupPanelProps): JSX.Element {
  const types = useMemo(
    () => new Map<string, DuckDbType>(columns.map((column) => [column.name, column.type])),
    [columns],
  );

  const patch = (changes: Partial<GroupState>): void => onChange({ ...state, ...changes });

  const apply = (): void => {
    const next: GroupState = { ...state, enabled: true };
    const active = isGroupActive(next);
    // Persist `enabled` too, or the authored state stays inactive and the
    // GROUPED banner has nothing to describe.
    onChange(active ? next : { ...state, enabled: false });
    onApply(active ? next : null);
  };

  return (
    <section
      className="flex flex-col border-b border-line bg-surface"
      aria-label="Group and aggregate"
      data-testid="group-panel"
      onKeyDown={(event) => {
        if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
          event.preventDefault();
          apply();
        }
      }}
    >
      <div className="flex items-center gap-2 border-b border-line px-2.5 py-1.5">
        <span className="eyebrow">Group by</span>
        <button
          type="button"
          className="icon-btn h-5"
          aria-label="Add group column"
          onClick={() => patch({ groups: [...state.groups, newGroupColumn(columns[0]?.name ?? '')] })}
        >
          +
        </button>
        <button
          type="button"
          className="icon-btn ml-auto"
          onClick={onClose}
          aria-label="Close group panel"
        >
          ✕
        </button>
      </div>

      <div className="max-h-[40vh] overflow-auto px-2.5 py-1.5">
        {state.groups.length === 0 ? (
          <p className="py-0.5 text-[11px] text-muted">
            No group columns — aggregates will run over the whole result.
          </p>
        ) : (
          <div className="space-y-1">
            {state.groups.map((entry, index) => (
              <div key={entry.id} className="flex items-center gap-1.5">
                <span className="tnum w-4 shrink-0 text-[10px] text-faint">{index + 1}</span>
                <ColumnPicker
                  columns={columns}
                  value={entry.column}
                  ariaLabel={`Group column ${index + 1}`}
                  onChange={(column) =>
                    patch({
                      groups: state.groups.map((g) => (g.id === entry.id ? { ...g, column } : g)),
                    })
                  }
                />
                <button
                  type="button"
                  className="icon-btn h-5"
                  aria-label={`Move ${entry.column} earlier`}
                  disabled={index === 0}
                  onClick={() => patch({ groups: moveGroupColumn(state.groups, entry.id, -1) })}
                >
                  ↑
                </button>
                <button
                  type="button"
                  className="icon-btn h-5"
                  aria-label={`Move ${entry.column} later`}
                  disabled={index === state.groups.length - 1}
                  onClick={() => patch({ groups: moveGroupColumn(state.groups, entry.id, 1) })}
                >
                  ↓
                </button>
                <button
                  type="button"
                  className="icon-btn h-5 hover:!text-danger"
                  aria-label={`Remove group column ${entry.column}`}
                  onClick={() =>
                    patch({ groups: state.groups.filter((g) => g.id !== entry.id) })
                  }
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="mt-2 flex items-center gap-2 border-t border-line pt-2">
          <span className="eyebrow">Aggregates</span>
          <button
            type="button"
            className="icon-btn h-5"
            aria-label="Add aggregate"
            onClick={() =>
              patch({
                aggregates: [
                  ...state.aggregates,
                  newAggregate(columns[0]?.name ?? '', columns[0]?.type),
                ],
              })
            }
          >
            +
          </button>
        </div>

        {state.aggregates.length === 0 ? (
          <p className="py-0.5 text-[11px] text-muted">
            None — a row count is used if you apply with no aggregates.
          </p>
        ) : (
          <div className="mt-1 space-y-1">
            {state.aggregates.map((spec, index) => {
              const options = aggregatesFor(types.get(spec.column));
              return (
                <div key={spec.id} className="flex items-center gap-1.5">
                  <select
                    className="field w-28"
                    aria-label={`Aggregate ${index + 1} function`}
                    value={spec.fn}
                    onChange={(event) =>
                      patch({
                        aggregates: state.aggregates.map((a) =>
                          a.id === spec.id ? { ...a, fn: event.target.value as AggregateFn } : a,
                        ),
                      })
                    }
                  >
                    {options.map((entry) => (
                      <option key={entry.value} value={entry.value}>
                        {entry.label}
                      </option>
                    ))}
                  </select>
                  <span className="text-[10px] text-faint">of</span>
                  <ColumnPicker
                    columns={columns}
                    value={spec.column}
                    ariaLabel={`Aggregate ${index + 1} column`}
                    onChange={(column) =>
                      patch({
                        aggregates: state.aggregates.map((a) =>
                          a.id === spec.id
                            ? { ...a, column, fn: coerceAggregate(a.fn, types.get(column)) }
                            : a,
                        ),
                      })
                    }
                  />
                  <button
                    type="button"
                    className="icon-btn h-5 hover:!text-danger"
                    aria-label={`Remove aggregate ${index + 1}`}
                    onClick={() =>
                      patch({ aggregates: state.aggregates.filter((a) => a.id !== spec.id) })
                    }
                  >
                    ✕
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 border-t border-line px-2.5 py-1.5">
        {mode !== 'local' ? (
          <span className="text-[10px] text-remote">
            No local cache — grouping runs on the server.
          </span>
        ) : (
          <span className="text-[10px] text-local">
            Runs in this tab against the cached rows. No network.
          </span>
        )}

        <div className="ml-auto flex items-center gap-1">
          <button
            type="button"
            className="btn"
            onClick={() => {
              onChange({ enabled: false, groups: [], aggregates: [] });
              onApply(null);
            }}
          >
            Ungroup
          </button>
          <button type="button" className="btn btn-run" onClick={apply}>
            Apply grouping
          </button>
        </div>
      </div>
    </section>
  );
}
